/* Step 11c-3 · find 33 (Mars Live Run Log) — the related-party "Scan GL" result. The scan reads the CURRENT period only and
   stores the lines grouped by party (backend/related_parties.py summarise_hits): per party the number of lines and
   vouchers, Σ debit and Σ credit, the first / last date, and its first lines — each with its date (DD-MMM-YYYY), account
   and signed amount (debit − credit). Before, every line read "· · 0" and the total 0.00 (the screen read date / account /
   amount, the scan stored entry_date / account_code / debit / credit), and FY2024 vouchers were included (24,677 hits). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useState } from "react";
import { fmtNumber, fmtCount } from "@/components/Amount";
import { fmtDate, fmtDateTime } from "@/lib/dt";
import { asArray } from "@/lib/asArray";

const fill = (s, m) => Object.entries(m).reduce((a, [k, v]) => a.split(`{${k}}`).join(String(v)), String(s || ""));

export default function RpScanResult({ scan, parties, t }) {
  const [open, setOpen] = useState({});
  const scanCols = useResizableColumns({ storageKey: "rp-scan", defaults: [240, 90, 100, 130, 130, 200], mins: [140, 64, 72, 100, 100, 140] });
  if (!scan) return null;
  const groups = asArray(scan.groups);
  const shown = groups.reduce((n, g) => n + asArray(g.hits).length, 0);
  return (
    <div className="mt-2 text-xs" data-testid="rp-scan-result">
      <div className="font-medium">{(t("rp.scanResultOn") || "").replace("{date}", fmtDateTime(scan.updated_at))}</div>
      {scan.outdated && (
        <div className="mt-1 rounded border border-amber-300 bg-amber-50 p-1 text-amber-900" data-testid="rp-scan-outdated">
          {t("s11c3Rp.scanOutdated")}</div>
      )}
      {groups.length === 0 ? (
        <div className="text-slate-600" data-testid="rp-scan-no-hits">
          {(t("rp.noGlEntriesFor") || "").replace("{party}", asArray(parties).join(", ") || "—")}
        </div>
      ) : (
        <>
          <div data-testid="rp-scan-hits">
            {fill(t("s11c3Rp.scanSummary"), { lines: fmtCount(scan.total_lines || 0),
              vouchers: fmtCount(scan.total_vouchers || 0), parties: groups.length,
              debit: fmtNumber(scan.debit || 0), credit: fmtNumber(scan.credit || 0) })}
          </div>
          {scan.capped && (
            <div className="text-muted-foreground" data-testid="rp-scan-capped">
              {fill(t("s11c3Rp.scanCapped"), { shown: fmtCount(shown),
                total: fmtCount(scan.total_lines || 0) })}
            </div>
          )}
          <ResizableTable cols={scanCols} wrap className="mt-1 w-full rounded border text-[11px]" data-testid="rp-scan-groups">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <ResizableTh index={0} className="p-1 text-start">{t("rp.relatedParty")}</ResizableTh>
                <ResizableTh index={1} className="p-1 text-end">{t("s11c3Rp.lines")}</ResizableTh>
                <ResizableTh index={2} className="p-1 text-end">{t("s11c3Rp.vouchers")}</ResizableTh>
                <ResizableTh index={3} className="p-1 text-end">{t("s11c3Rp.debit")}</ResizableTh>
                <ResizableTh index={4} className="p-1 text-end">{t("s11c3Rp.credit")}</ResizableTh>
                <ResizableTh index={5} className="p-1 text-start">{t("s11c3Rp.dates")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <React.Fragment key={g.party}>
                  <tr className="cursor-pointer border-t hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`rp-scan-group-${g.party}`}
                      onClick={() => setOpen((o) => ({ ...o, [g.party]: !o[g.party] }))}>
                    <td className="p-1">{open[g.party] ? "▾" : "▸"} {g.party}</td>
                    <td className="p-1 text-end tabular-nums" dir="ltr">{fmtCount(g.lines || 0)}</td>
                    <td className="p-1 text-end tabular-nums" dir="ltr">{fmtCount(g.vouchers || 0)}</td>
                    <td className="p-1 text-end tabular-nums" dir="ltr">{fmtNumber(g.debit || 0)}</td>
                    <td className="p-1 text-end tabular-nums" dir="ltr">{fmtNumber(g.credit || 0)}</td>
                    <td className="p-1 whitespace-nowrap" dir="ltr">{fmtDate(g.first_date) || "—"} – {fmtDate(g.last_date) || "—"}</td>
                  </tr>
                  {open[g.party] && asArray(g.hits).map((h, i) => (
                    <tr key={i} className="bg-slate-50/60 dark:bg-slate-900/40" data-testid={`rp-scan-line-${g.party}-${i}`}>
                      <td className="p-1 ps-5 whitespace-nowrap" dir="ltr">{fmtDate(h.entry_date || h.date) || "—"} · {h.voucher_no || "—"}</td>
                      <td className="p-1" colSpan={2}><bdi>{h.account || h.account_code || "—"}</bdi></td>
                      <td className="p-1 text-end tabular-nums" dir="ltr" colSpan={2}>{fmtNumber(h.amount ?? ((h.debit || 0) - (h.credit || 0)))}</td>
                      <td className="p-1 truncate" title={h.description || ""}>{h.description || ""}</td>
                    </tr>
                  ))}
                  {open[g.party] && g.lines > asArray(g.hits).length && (
                    <tr><td colSpan={6} className="p-1 ps-5 text-muted-foreground">
                      {fill(t("s11c3Rp.partyMore"), { shown: asArray(g.hits).length, total: fmtCount(g.lines) })}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </ResizableTable>
        </>
      )}
    </div>
  );
}
